/**
 * Code blocks for the Tiptap markdown editor: mermaid preview, lazy syntax
 * highlighting, and a language selector.
 *
 * This replaces the earlier React NodeView (`ReactNodeViewRenderer`) with a
 * plain synchronous ProseMirror NodeView, and that choice is load-bearing:
 * React mounts its portal a tick after ProseMirror creates the view, so the
 * `contentDOM` wasn't attached when PM tried to place the selection into a
 * freshly created block. Typing ```ts + Enter created the code block but the
 * caret escaped to the paragraph after it. With the DOM built synchronously
 * in the constructor, selection restore succeeds and the caret lands inside
 * the fence.
 *
 * Behavior:
 *  - Every block renders an editable `<pre><code>` (the contentDOM) plus a
 *    hover control strip: a language `<select>` (the supported grammar list
 *    + the previewable languages + plain text, sorted) and a copy-source
 *    button.
 *  - A language with a {@link CodeBlockPreview} renders that preview in place
 *    of the source: preview by default for blocks that already have source,
 *    source-first for empty ones (so a freshly typed fence is editable
 *    immediately); a toggle flips views. ```mermaid is built in; a consumer
 *    adds its own languages through the `previews` option (Bear registers an
 *    `svg` floorplan preview that way). One switch, one debounce, one
 *    stale-render guard, whatever the language.
 *  - Mermaid diagrams render via `beautiful-mermaid`, themed from the hearth
 *    palette's two base colors; diagram types it can't parse fall back to the
 *    full `mermaid` runtime. Both load lazily on first use.
 *  - Languages with no preview get lowlight syntax highlighting with per-language
 *    grammar chunks (see syntax-highlight.ts). When a grammar finishes
 *    loading, the view nudges its block with a history-free same-attrs
 *    `setNodeMarkup` — a doc-changing step that encapsulates the node, which
 *    is the condition the lowlight plugin recomputes decorations on. The
 *    nudge serializes to identical markdown, so undo and Automerge never
 *    see it.
 *
 * Storage is untouched: blocks round-trip as ordinary fenced code with the
 * language as the info string.
 */
import type { Editor } from "@tiptap/core";
import {
  CodeBlockLowlight,
  type CodeBlockLowlightOptions,
} from "@tiptap/extension-code-block-lowlight";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { NodeView, ViewMutationRecord } from "@tiptap/pm/view";

import {
  canonicalLanguage,
  editorLowlight,
  ensureGrammar,
  grammarRegistered,
  SUPPORTED_LANGUAGES,
} from "./syntax-highlight";

// ---------------------------------------------------------------------------
// Lazy beautiful-mermaid module (shared across all views)
// ---------------------------------------------------------------------------

let beautifulMermaid: typeof import("beautiful-mermaid") | null = null;

/** Lazy-load beautiful-mermaid. Unlike the `mermaid` runtime it replaces, it
 *  parses and renders the diagram itself (elkjs + entities, no ~1MB mermaid
 *  bundle) and emits a self-contained SVG whose colors are derived from just a
 *  `bg`/`fg` pair via `color-mix`. */
async function loadMermaid(): Promise<typeof import("beautiful-mermaid")> {
  if (!beautifulMermaid) {
    beautifulMermaid = await import("beautiful-mermaid");
  }
  return beautifulMermaid;
}

// Two-color themes derived from the hearth colorscheme (warm paper + ink).
// beautiful-mermaid derives every other role (lines, arrows, node fills,
// strokes, muted text) from these two via `color-mix`, so this is the whole
// "theme". Values mirror `--background` / `--foreground` in
// packages/hearth/src/index.css — keep them in sync if that palette moves.
const HEARTH_LIGHT = { bg: "hsl(36 49% 96%)", fg: "hsl(26 14% 10%)" } as const;
const HEARTH_DARK = { bg: "hsl(26 14% 9%)", fg: "hsl(36 49% 96%)" } as const;

/** Whether the editor is currently in a dark context. Both apps theme purely
 *  via `prefers-color-scheme`. */
function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

// ---------------------------------------------------------------------------
// Standard mermaid fallback (shared across all views)
// ---------------------------------------------------------------------------
//
// beautiful-mermaid only implements the common diagram types (flowchart,
// sequence, class, ER, state, xychart). Exotic ones (gantt, pie, mindmap,
// gitGraph, journey, timeline, sankey, C4, …) throw at parse time; we catch
// that and fall back to the full mermaid runtime so they still render. It also
// rescues any diagram beautiful-mermaid mis-parses. The ~1MB bundle only loads
// when a diagram actually needs it.

let mermaidModule: typeof import("mermaid").default | null = null;

/** Lazy-load mermaid and initialize its `base` theme from the hearth palette so
 *  fallback diagrams roughly match the beautiful-mermaid ones. mermaid's color
 *  math (khroma) parses the same `hsl()` strings we hand beautiful-mermaid, so
 *  the fallback reuses HEARTH_LIGHT/HEARTH_DARK — one source, no drift.
 *  `initialize` just merges config, so re-applying it per render keeps the theme
 *  current. `securityLevel: "strict"` sanitizes diagram-authored HTML/JS out of
 *  the SVG, since diagram source can come from peers/agents. */
async function loadMermaidFallback(): Promise<
  typeof import("mermaid").default
> {
  if (!mermaidModule) {
    mermaidModule = (await import("mermaid")).default;
  }
  const dark = prefersDark();
  const { bg, fg } = dark ? HEARTH_DARK : HEARTH_LIGHT;
  mermaidModule.initialize({
    startOnLoad: false,
    securityLevel: "strict",
    theme: "base",
    themeVariables: {
      darkMode: dark,
      background: bg,
      mainBkg: bg,
      primaryColor: bg,
      primaryTextColor: fg,
      primaryBorderColor: fg,
      secondaryColor: bg,
      tertiaryColor: bg,
      lineColor: fg,
      textColor: fg,
    },
  });
  return mermaidModule;
}

let mermaidRenderSeq = 0;

// ---------------------------------------------------------------------------
// Previews
// ---------------------------------------------------------------------------

/** What a preview renderer is handed: the fence body, plus the one verb it
 *  needs to write an out-of-editor edit back into its own block. */
export interface CodeBlockPreviewContext {
  /** The block's source, trimmed. Never empty (an empty block shows source). */
  source: string;
  /** Replace the block's source, e.g. after editing it in another surface. */
  replaceSource: (next: string) => void;
}

/** An icon button a preview contributes to the block's hover control strip,
 *  alongside the built-in copy and toggle (rather than overlaying them). */
export interface CodeBlockPreviewAction {
  title: string;
  /** Inline SVG markup for the 16px glyph (the strip is a vanilla NodeView). */
  icon: string;
  run(ctx: CodeBlockPreviewContext): void;
}

/**
 * Renders one fence language as something other than its source.
 *
 * `render` returns a **detached** node rather than painting into a container:
 * the NodeView owns when (and whether) a resolved render is mounted, so a slow
 * render that lost its race is dropped instead of clobbering a newer one. A
 * throw renders the error box.
 */
export interface CodeBlockPreview {
  render(ctx: CodeBlockPreviewContext): Node | Promise<Node>;
  /** Release whatever `render` attached to the node (a React root, a
   *  listener). Called when the node is replaced, dropped, or destroyed. */
  destroy?: (node: Node) => void;
  /** Extra control-strip buttons shown while this language is selected. */
  actions?: readonly CodeBlockPreviewAction[];
}

export type CodeBlockPreviews = Record<string, CodeBlockPreview>;

const mermaidPreview: CodeBlockPreview = {
  async render({ source }) {
    const host = document.createElement("div");
    try {
      const { renderMermaidSVGAsync } = await loadMermaid();
      // Async variant so ELK-laid-out diagrams resolve fully. Themed from the
      // hearth palette's two base colors; beautiful-mermaid derives the rest
      // via color-mix. Sanitized: beautiful-mermaid escapes text.
      host.innerHTML = await renderMermaidSVGAsync(source, {
        ...(prefersDark() ? HEARTH_DARK : HEARTH_LIGHT),
      });
      return host;
    } catch {
      // A diagram type beautiful-mermaid can't parse (or mis-parses): fall back
      // to the full mermaid runtime. `parse` throws on genuine syntax errors
      // without leaving stray DOM, so those reach the caller's error box.
      const mermaid = await loadMermaidFallback();
      await mermaid.parse(source);
      const { svg } = await mermaid.render(
        `forest-mermaid-${mermaidRenderSeq++}`,
        source,
      );
      host.innerHTML = svg;
      return host;
    }
  },
};

// ---------------------------------------------------------------------------
// NodeView
// ---------------------------------------------------------------------------

// Two skins for the hover controls, matched to the surface they float over:
// code blocks render on the typography plugin's dark <pre> in both themes, so
// theme tokens (bg-card etc.) would glare; the mermaid preview now uses the
// hearth palette (bg tracks light/dark), so theme tokens are right over it.
const BUTTON_BASE = "rounded-md border p-1.5 shadow-sm";
const BUTTON_ON_DARK =
  "border-white/10 bg-white/10 text-zinc-400 hover:bg-white/20 hover:text-zinc-100";
const BUTTON_ON_THEME =
  "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground";

// Lucide glyphs inlined — a vanilla NodeView can't render <Eye/>, and
// importing lucide's DOM build for four icons isn't worth it.
const EYE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>';
const CODE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>';
const COPY_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>';
const CHECK_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

class CodeBlockView implements NodeView {
  dom: HTMLDivElement;
  contentDOM: HTMLElement;

  private node: ProseMirrorNode;
  private readonly editor: Editor;
  private readonly getPos: () => number | undefined;
  private readonly previews: CodeBlockPreviews;

  private pre: HTMLPreElement;
  private preview: HTMLDivElement;
  private controls: HTMLDivElement;
  private select: HTMLSelectElement;
  private copyButton: HTMLButtonElement;
  private toggleButton: HTMLButtonElement;

  /** Previewable languages only: whether the source (not the preview) is
   *  showing. Blocks that start empty open in source mode so a freshly typed
   *  fence is immediately editable. */
  private showSource: boolean;
  /** The mounted preview and the renderer that produced it, so it can be torn
   *  down even after the block's language has moved on. */
  private mounted: { node: Node; owner: CodeBlockPreview } | null = null;
  /** Preview-contributed strip buttons and the renderer that supplied them. */
  private actionButtons: HTMLButtonElement[] = [];
  private actionsFrom: CodeBlockPreview | null = null;
  private renderTimer: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic token so a stale async preview render never clobbers a newer
   *  one (renders resolve out of order while typing). */
  private renderToken = 0;
  private destroyed = false;

  constructor(
    node: ProseMirrorNode,
    editor: Editor,
    getPos: () => number | undefined,
    previews: CodeBlockPreviews,
  ) {
    this.node = node;
    this.editor = editor;
    this.getPos = getPos;
    this.previews = previews;
    this.showSource = node.textContent.trim() === "";

    this.dom = document.createElement("div");
    this.dom.className = "group relative";

    this.pre = document.createElement("pre");
    this.contentDOM = document.createElement("code");
    this.pre.appendChild(this.contentDOM);
    this.dom.appendChild(this.pre);

    this.preview = document.createElement("div");
    this.preview.className =
      "code-block-preview flex justify-center [&_svg]:max-w-full";
    this.preview.contentEditable = "false";
    this.dom.appendChild(this.preview);

    // Hover controls: language selector + preview/source toggle.
    this.controls = document.createElement("div");
    this.controls.className =
      "absolute right-2 top-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100";
    this.controls.contentEditable = "false";

    this.select = document.createElement("select");
    this.select.title = "Code block language";
    this.select.setAttribute("aria-label", "Code block language");
    const languages = [
      ...new Set([...SUPPORTED_LANGUAGES, ...Object.keys(previews)]),
    ].sort();
    for (const value of ["", ...languages]) {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = value === "" ? "plain" : value;
      this.select.appendChild(option);
    }
    this.select.addEventListener("change", () => {
      const pos = this.getPos();
      if (pos === undefined || this.editor.isDestroyed) return;
      const language = this.select.value || null;
      this.editor.view.dispatch(
        this.editor.state.tr.setNodeMarkup(pos, undefined, {
          ...this.node.attrs,
          language,
        }),
      );
    });
    this.controls.appendChild(this.select);

    this.copyButton = document.createElement("button");
    const copyButton = this.copyButton;
    copyButton.type = "button";
    copyButton.innerHTML = COPY_SVG;
    copyButton.title = "Copy code";
    copyButton.setAttribute("aria-label", "Copy code");
    copyButton.addEventListener("click", () => {
      void navigator.clipboard?.writeText(this.node.textContent).then(() => {
        // Brief confirmation, then back to the copy glyph.
        copyButton.innerHTML = CHECK_SVG;
        setTimeout(() => {
          if (!this.destroyed) copyButton.innerHTML = COPY_SVG;
        }, 1200);
      });
    });
    this.controls.appendChild(copyButton);

    this.toggleButton = document.createElement("button");
    this.toggleButton.type = "button";
    this.toggleButton.addEventListener("click", () => {
      this.showSource = !this.showSource;
      this.sync();
      if (this.showSource) {
        // Put the caret into the revealed source so editing can start.
        const pos = this.getPos();
        if (pos !== undefined && !this.editor.isDestroyed) {
          this.editor.commands.focus(pos + 1);
        }
      }
    });
    this.controls.appendChild(this.toggleButton);
    this.dom.appendChild(this.controls);

    this.sync();
  }

  private get language(): string | null {
    return (this.node.attrs.language as string | null) ?? null;
  }

  /** The preview renderer for this block's language, if any. */
  private get renderer(): CodeBlockPreview | null {
    const { language } = this;
    return (language && this.previews[language]) || null;
  }

  /** Write a preview's out-of-editor edit back into this block's source. */
  private replaceSource(next: string): void {
    const pos = this.getPos();
    if (pos === undefined || this.editor.isDestroyed) return;
    const from = pos + 1;
    const { schema, tr } = this.editor.state;
    this.editor.view.dispatch(
      tr.replaceWith(
        from,
        from + this.node.content.size,
        next ? schema.text(next) : [],
      ),
    );
  }

  /** Unmount whatever preview is showing, giving its renderer the chance to
   *  release what it attached. */
  private clearPreview(): void {
    if (this.mounted) {
      this.mounted.owner.destroy?.(this.mounted.node);
      this.mounted = null;
    }
    this.preview.replaceChildren();
  }

  /** Rebuild the preview-contributed buttons when the renderer changes,
   *  keeping them between copy and toggle in the strip. */
  private syncActions(): void {
    if (this.actionsFrom === this.renderer) return;
    this.actionsFrom = this.renderer;
    for (const button of this.actionButtons) button.remove();
    this.actionButtons = [];
    for (const action of this.renderer?.actions ?? []) {
      const button = document.createElement("button");
      button.type = "button";
      button.innerHTML = action.icon;
      button.title = action.title;
      button.setAttribute("aria-label", action.title);
      button.addEventListener("click", () => {
        action.run({
          source: this.node.textContent.trim(),
          replaceSource: (next) => this.replaceSource(next),
        });
      });
      this.controls.insertBefore(button, this.toggleButton);
      this.actionButtons.push(button);
    }
  }

  /** Reflect the current node + toggle state into the DOM. */
  private sync(): void {
    const { language } = this;
    // Assign only on change: a same-value className write still emits a
    // MutationObserver record, and observer noise on the contentDOM is what
    // the ignoreMutation above exists to keep out of PM's way.
    const codeClass = language ? `language-${language}` : "";
    if (this.contentDOM.className !== codeClass) {
      this.contentDOM.className = codeClass;
    }
    // Show aliased fences (` ```ts `) as their canonical grammar
    // (`typescript`) so the selector reflects what's actually highlighting.
    // A previewable language names itself — `svg` is an alias of the `xml`
    // grammar, but it's `svg` that decides what's rendered.
    this.select.value = language
      ? this.renderer
        ? language
        : (canonicalLanguage(language) ?? "")
      : "";

    this.syncActions();
    const showPreview = this.renderer !== null && !this.showSource;
    this.pre.style.display = showPreview ? "none" : "";
    this.preview.style.display = showPreview ? "" : "none";
    this.toggleButton.style.display = this.renderer ? "" : "none";
    this.toggleButton.innerHTML = this.showSource ? EYE_SVG : CODE_SVG;
    this.toggleButton.title = this.showSource ? "Show preview" : "Show source";
    this.toggleButton.setAttribute("aria-label", this.toggleButton.title);

    // Skin the controls for the surface they're floating over: the dark
    // <pre> for source views, the themed preview for a rendered one. The
    // select shares the icon buttons' skin and height (text-xs line-height
    // 1rem + py-1.5 matches their 16px icon + p-1.5).
    const skin = showPreview ? BUTTON_ON_THEME : BUTTON_ON_DARK;
    this.select.className = `${BUTTON_BASE} ${skin} cursor-pointer appearance-none px-2 py-1.5 font-mono text-xs`;
    this.copyButton.className = `${BUTTON_BASE} ${skin}`;
    this.toggleButton.className = `${BUTTON_BASE} ${skin}`;
    for (const button of this.actionButtons) {
      button.className = `${BUTTON_BASE} ${skin}`;
    }

    if (showPreview) {
      this.schedulePreviewRender();
    } else {
      // Switched away from the rendered preview (source toggle or a language
      // change): cancel any pending render and invalidate the token so a stale
      // async render can't paint into the now-hidden container.
      if (this.renderTimer !== null) {
        clearTimeout(this.renderTimer);
        this.renderTimer = null;
      }
      this.renderToken++;
      this.clearPreview();
      if (language && !grammarRegistered(language)) {
        this.loadGrammarAndRefresh(language);
      }
    }
  }

  /** Debounced preview render, mounted into the preview container. */
  private schedulePreviewRender(): void {
    if (this.renderTimer !== null) clearTimeout(this.renderTimer);
    const renderer = this.renderer;
    const language = this.language;
    const source = this.node.textContent.trim();
    if (!renderer || !source) {
      this.clearPreview();
      return;
    }
    const token = ++this.renderToken;
    this.renderTimer = setTimeout(() => {
      void (async () => {
        try {
          const rendered = await renderer.render({
            source,
            replaceSource: (next) => this.replaceSource(next),
          });
          if (this.destroyed || token !== this.renderToken) {
            renderer.destroy?.(rendered);
            return;
          }
          this.clearPreview();
          this.preview.appendChild(rendered);
          this.mounted = { node: rendered, owner: renderer };
        } catch (err) {
          if (this.destroyed || token !== this.renderToken) return;
          const message = err instanceof Error ? err.message : "Invalid source";
          this.clearPreview();
          const box = document.createElement("div");
          box.className =
            "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive whitespace-pre-wrap font-mono";
          box.textContent = `${language} preview failed\n${message}`;
          this.preview.appendChild(box);
        }
      })();
    }, 300);
  }

  /** Load the grammar chunk, then nudge this block so the lowlight plugin
   *  recomputes decorations (see module docs). */
  private loadGrammarAndRefresh(language: string): void {
    void ensureGrammar(language).then((loaded) => {
      if (!loaded || this.destroyed || this.editor.isDestroyed) return;
      const pos = this.getPos();
      if (pos === undefined) return;
      const current = this.editor.state.doc.nodeAt(pos);
      if (!current || current.type !== this.node.type) return;
      this.editor.view.dispatch(
        this.editor.state.tr
          .setNodeMarkup(pos, undefined, current.attrs)
          .setMeta("addToHistory", false),
      );
    });
  }

  update(node: ProseMirrorNode): boolean {
    if (node.type !== this.node.type) return false;
    const previous = this.node;
    this.node = node;
    if (
      node.attrs.language !== previous.attrs.language ||
      node.textContent !== previous.textContent
    ) {
      this.sync();
    }
    return true;
  }

  /** PM must not reparse mutations outside the editable code content — the
   *  preview SVG, control strip, and style/class flips are all ours. */
  ignoreMutation(mutation: ViewMutationRecord): boolean {
    // Attribute mutations are never document content here — code block text
    // carries no attributes — and `sync()` flips classes/styles on the
    // wrapper AND the contentDOM (`language-*`). Letting one of those
    // through marks the view dirty and PM destroys/recreates it, which
    // resets the mermaid source toggle mid-click.
    if (mutation.type === "attributes") return true;
    return !(
      this.contentDOM === mutation.target ||
      this.contentDOM.contains(mutation.target)
    );
  }

  /** Keep PM's own mouse/keyboard handling away from the controls. */
  stopEvent(event: Event): boolean {
    const target = event.target;
    return target instanceof Node && this.controls.contains(target);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.renderTimer !== null) clearTimeout(this.renderTimer);
    this.clearPreview();
  }
}

/**
 * Code block with per-language previews, lazily-loaded syntax highlighting,
 * and a language selector. Drop-in replacement for StarterKit's `codeBlock`
 * (disable that one and register this instead).
 *
 * `previews` registers consumer-specific languages; `mermaid` is built in and
 * cannot be overridden away.
 */
export const EditorCodeBlock = CodeBlockLowlight.extend<
  CodeBlockLowlightOptions & { previews: CodeBlockPreviews }
>({
  addOptions() {
    // `parent` is always present when extending a concrete extension; the
    // non-null keeps the spread from widening every option to optional.
    return {
      ...this.parent!(),
      lowlight: editorLowlight,
      previews: {},
    };
  },

  addNodeView() {
    const previews = { ...this.options.previews, mermaid: mermaidPreview };
    return ({ node, editor, getPos }) =>
      new CodeBlockView(node, editor, getPos, previews);
  },
});
